/**
 * Collaboration WebSocket Service
 * Handles real-time collaborative learning sessions
 */

// Browser-compatible EventEmitter implementation
class EventEmitter {
  private events: { [event: string]: Function[] } = {};

  on(event: string, listener: Function) {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(listener);
  }

  emit(event: string, ...args: any[]) {
    if (this.events[event]) {
      this.events[event].forEach(listener => listener(...args));
    }
  }

  off(event: string, listener: Function) {
    if (this.events[event]) {
      this.events[event] = this.events[event].filter(l => l !== listener);
    }
  }

  removeAllListeners(event?: string) {
    if (event) {
      delete this.events[event];
    } else {
      this.events = {};
    }
  }
}

export type CollaborationMessageType =
  | 'connection_established'
  | 'heartbeat_ack'
  | 'participant_joined'
  | 'participant_left'
  | 'cursor_update'
  | 'selection_update'
  | 'edit_operation'
  | 'edit_conflict'
  | 'annotation_created'
  | 'annotation_updated'
  | 'whiteboard_operation'
  | 'voice_operation'
  | 'sync_response'
  | 'session_ended'
  | 'error';

export interface CollaborationMessage {
  type: CollaborationMessageType;
  data?: any;
  timestamp: string;
  userId?: string;
  sessionId?: string;
}

export interface ParticipantInfo {
  userId: string;
  userName: string;
  userRole: 'student' | 'teacher' | 'parent' | 'tutor';
  capabilities: string[];
  joinedAt: string;
}

export interface CursorPosition {
  userId: string;
  userName: string;
  x: number;
  y: number;
  elementId?: string;
  timestamp: string;
}

export interface SelectionRange {
  userId: string;
  userName: string;
  startOffset: number;
  endOffset: number;
  elementId: string;
  selectedText?: string;
  timestamp: string;
}

export interface EditOperation {
  operationId: string;
  userId: string;
  type: 'insert' | 'delete' | 'retain' | 'format';
  position: number;
  content?: string;
  length?: number;
  attributes?: Record<string, any>;
  timestamp: string;
}

export interface Annotation {
  annotationId: string;
  userId: string;
  userName: string;
  type: 'note' | 'highlight' | 'question' | 'suggestion';
  elementId: string;
  position: { x: number; y: number };
  content: string;
  color?: string;
  timestamp: string;
}

export interface WhiteboardOperation {
  operationId: string;
  userId: string;
  type: 'draw' | 'erase' | 'shape' | 'text' | 'clear';
  points?: Array<{ x: number; y: number }>;
  shapeData?: any;
  strokeColor?: string;
  strokeWidth?: number;
  timestamp: string;
}

export interface SessionState {
  sessionId: string;
  participants: ParticipantInfo[];
  documents: any[];
  whiteboardState: any;
  enabledFeatures: string[];
  sessionStatus: 'active' | 'ended' | 'paused';
}

class CollaborationWebSocketService extends EventEmitter {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private isConnected = false;
  private currentSessionId: string | null = null;
  private currentUserId: string | null = null;
  private sessionState: SessionState | null = null;

  /**
   * Connect to collaboration session
   */
  connectToSession(
    sessionId: string,
    token: string,
    mode: 'participant' | 'observer' | 'moderator' = 'participant'
  ): void {
    const wsUrl = import.meta.env.VITE_WS_URL || 'wss://staging.sigma08.com';
    const url = `${wsUrl}/api/v1/ws/collaboration/${sessionId}?token=${token}&mode=${mode}`;

    this.currentSessionId = sessionId;
    this.connect(url);
  }

  /**
   * Connect to session state management
   */
  connectToSessionState(userId: string, token: string): void {
    const wsUrl = import.meta.env.VITE_WS_URL || 'wss://staging.sigma08.com';
    const url = `${wsUrl}/api/v1/ws/session-state/${userId}?token=${token}`;

    this.currentUserId = userId;
    this.connect(url);
  }

  private connect(url: string): void {
    try {
      // Close existing connection if any
      if (this.ws) {
        this.disconnect();
      }

      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.emit('connected');
        this.startHeartbeat();
      };

      this.ws.onmessage = event => {
        try {
          const message: CollaborationMessage = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (error) {
          console.error('Failed to parse collaboration WebSocket message:', error);
        }
      };

      this.ws.onerror = error => {
        console.error('Collaboration WebSocket error:', error);
        this.emit('error', error);
      };

      this.ws.onclose = event => {
        this.isConnected = false;
        this.stopHeartbeat();
        this.emit('disconnected');

        // Attempt reconnection if not intentional close
        if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.attemptReconnect(url);
        }
      };
    } catch (error) {
      console.error('Failed to connect collaboration WebSocket:', error);
      this.emit('error', error);
    }
  }

  private handleMessage(message: CollaborationMessage): void {
    switch (message.type) {
      case 'connection_established':
        this.sessionState = message.data.sessionState;
        this.emit('session_state_updated', this.sessionState);
        this.emit('connection_established', message.data);
        break;

      case 'heartbeat_ack':
        // Heartbeat response received
        break;

      case 'participant_joined':
        if (this.sessionState) {
          this.sessionState.participants.push(message.data.participant);
          this.emit('session_state_updated', this.sessionState);
        }
        this.emit('participant_joined', message.data.participant);
        break;

      case 'participant_left':
        if (this.sessionState) {
          this.sessionState.participants = this.sessionState.participants.filter(p => p.userId !== message.data.userId);
          this.emit('session_state_updated', this.sessionState);
        }
        this.emit('participant_left', message.data);
        break;

      case 'cursor_update':
        this.emit('cursor_moved', message.data);
        break;

      case 'selection_update':
        this.emit('selection_changed', message.data);
        break;

      case 'edit_operation':
        this.emit('document_edited', message.data);
        break;

      case 'edit_conflict':
        this.emit('edit_conflict', message.data);
        break;

      case 'annotation_created':
      case 'annotation_updated':
        this.emit('annotation_updated', message.data);
        break;

      case 'whiteboard_operation':
        this.emit('whiteboard_updated', message.data);
        break;

      case 'voice_operation':
        this.emit('voice_operation', message.data);
        break;

      case 'sync_response':
        this.emit('sync_response', message.data);
        break;

      case 'session_ended':
        this.sessionState = null;
        this.emit('session_ended', message.data);
        break;

      case 'error':
        this.emit('collaboration_error', message.data);
        break;

      default:
        console.warn('Unknown collaboration message type:', message.type);
    }
  }

  /**
   * Send cursor position update
   */
  updateCursor(position: Omit<CursorPosition, 'userId' | 'userName' | 'timestamp'>): void {
    this.send('cursor', position);
  }

  /**
   * Send text selection update
   */
  updateSelection(selection: Omit<SelectionRange, 'userId' | 'userName' | 'timestamp'>): void {
    this.send('selection', selection);
  }

  /**
   * Send edit operation
   */
  sendEditOperation(operation: Omit<EditOperation, 'userId' | 'timestamp'>): void {
    this.send('edit', operation);
  }

  /**
   * Create annotation
   */
  createAnnotation(annotation: Omit<Annotation, 'annotationId' | 'userId' | 'userName' | 'timestamp'>): void {
    this.send('annotation', {
      ...annotation,
      action: 'create',
    });
  }

  /**
   * Update annotation
   */
  updateAnnotation(annotationId: string, updates: Partial<Annotation>): void {
    this.send('annotation', {
      annotationId,
      action: 'update',
      updates,
    });
  }

  /**
   * Delete annotation
   */
  deleteAnnotation(annotationId: string): void {
    this.send('annotation', {
      annotationId,
      action: 'delete',
    });
  }

  /**
   * Send whiteboard operation
   */
  sendWhiteboardOperation(operation: Omit<WhiteboardOperation, 'userId' | 'timestamp'>): void {
    this.send('whiteboard', operation);
  }

  /**
   * Start voice chat
   */
  startVoiceChat(): void {
    this.send('voice', {
      action: 'start_chat',
    });
  }

  /**
   * End voice chat
   */
  endVoiceChat(): void {
    this.send('voice', {
      action: 'end_chat',
    });
  }

  /**
   * Mute/unmute microphone
   */
  toggleMute(muted: boolean): void {
    this.send('voice', {
      action: 'toggle_mute',
      muted,
    });
  }

  /**
   * Request session sync
   */
  requestSync(): void {
    this.send('sync', {
      requestType: 'full_sync',
    });
  }

  /**
   * Update session state
   */
  updateSessionState(stateUpdate: any): void {
    this.send('state_update', stateUpdate);
  }

  /**
   * Update user preferences
   */
  updatePreferences(preferences: any): void {
    this.send('preference_update', preferences);
  }

  private send(type: string, data?: any): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.error('Collaboration WebSocket is not connected');
      return;
    }

    const message = {
      type,
      data,
      timestamp: new Date().toISOString(),
      sessionId: this.currentSessionId,
      userId: this.currentUserId,
    };

    this.ws.send(JSON.stringify(message));
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (this.isConnected) {
        this.send('heartbeat');
      }
    }, 30000); // Heartbeat every 30 seconds
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private attemptReconnect(url: string): void {
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    setTimeout(() => {
      this.connect(url);
    }, delay);
  }

  /**
   * Manually disconnect WebSocket
   */
  disconnect(): void {
    this.stopHeartbeat();

    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }

    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.currentSessionId = null;
    this.currentUserId = null;
    this.sessionState = null;
  }

  /**
   * Get current session state
   */
  getSessionState(): SessionState | null {
    return this.sessionState;
  }

  /**
   * Check if currently connected
   */
  getConnectionStatus(): boolean {
    return this.isConnected;
  }

  /**
   * Get current session ID
   */
  getCurrentSessionId(): string | null {
    return this.currentSessionId;
  }

  /**
   * Get participants in current session
   */
  getParticipants(): ParticipantInfo[] {
    return this.sessionState?.participants || [];
  }

  /**
   * Check if user has capability
   */
  hasCapability(capability: string): boolean {
    if (!this.sessionState || !this.currentUserId) return false;

    const currentUser = this.sessionState.participants.find(p => p.userId === this.currentUserId);
    return currentUser?.capabilities.includes(capability) || false;
  }
}

// Export singleton instance
export const collaborationWebSocketService = new CollaborationWebSocketService();
export default collaborationWebSocketService;
